import { flow, types, getSnapshot, getParent, destroy } from "mobx-state-tree"
import Api from '../api/Api'
import State, {notLoaded, pending, error, done} from './State'
import _ from 'lodash'
import validate from 'validate.js'
import moment from 'moment'
import PaginationResponse from './PaginationResponse'
import { Transaction } from './TransactionStore';

const CustomDate = types.custom({
    fromSnapshot(snapshot) {
        return snapshot && moment(snapshot).toDate();
     },
     toSnapshot(value) {
        return value && moment(value).toISOString();
     },
     isTargetType(value) {
         return _.isDate(value);
     },
     getValidationMessage(snapshot) {
        return (!_.isNil(snapshot) && moment(snapshot).isValid()) ? "" : "Invalid date"
     }
 })

const Pickup = types.model({
    id: types.maybeNull(types.number),
    pickup_at: types.maybeNull(CustomDate),
    inventory_location: types.frozen(),
    done: types.optional(types.boolean, false),
    transactions: types.optional(types.array(Transaction), []),
})
.actions((self)=>({
    setInventoryLocation: (inventoryLocation)=>{
        self.inventory_location = inventoryLocation
    },
    setPickupAt: (pickupAt)=>{
        self.pickup_at = pickupAt;
    },
    remove: (item) => {
        getParent(self,2).remove(self)
    },
}))
.views(self => ({
    get totalWeight() {
        return self.transactions.reduce((val, transaction) => val + transaction.weight, 0);
    }
}))

const LogisticPlan = types.model({
    archived_at: types.maybeNull(CustomDate),
    created_at: types.maybeNull(CustomDate),
    pickups: types.optional(types.array(Pickup), []),
    saveState: types.optional(State, notLoaded),
    id: types.maybe(types.number),
    fetchExcelState: types.optional(State, notLoaded),
    destination: types.frozen(),
    carrier_name: types.maybeNull(types.string),
    carrier_driver_name: types.maybeNull(types.string),
    message: types.maybeNull(types.string),
    waste_type: types.maybeNull(types.string)
})
.volatile(self => ({
    excelData: undefined
}))
.actions((self)=>({
    addPickup: (pickup)=>{
        self.pickups.push(pickup);
    },
    setDestinationLocation: (inventoryLocation)=>{
        self.destination = inventoryLocation
    },
    setCarrierName: (carrierName)=>{
        self.carrier_name = carrierName
    },
    setCarrierDriverName: (carrierDriverName)=>{
        self.carrier_driver_name = carrierDriverName
    },
    setMessage: (message)=>{
        self.message = message
    },
    setWasteType: (waste_type)=>{
        self.waste_type = waste_type
    }, 
    remove: (item) => {
        destroy(item)
    },
    save: flow(function* save(accountId) {
        self.saveState = pending
        try {
            var snapshot = getSnapshot(self);
            var data = {
                account: accountId,
                destination: snapshot.destination.id,
                message: snapshot.message,
                carrier_name: snapshot.carrier_name,
                carrier_driver_name: snapshot.carrier_driver_name,
                waste_type: snapshot.waste_type
            };
            data.pickups = snapshot.pickups.map(pickup => {
                const data = {
                    inventory_location: pickup.inventory_location.id,
                    pickup_at: pickup.pickup_at,
                    done: pickup.done
                };
                if (pickup.id) {
                    data.id = pickup.id
                }
                return data;
            });

            _.isNil(self.id) ? yield Api.post(`/logistic_plans/`, data) : yield Api.patch(`/logistic_plans/${self.id}/`, data)
            self.saveState = done
        } catch (e) {
            console.error(e)    
            self.saveState = error
        }
    }),
    fetchExcel: flow(function* fetchExcel() {
        self.fetchExcelState = pending
        try {
            const response = yield Api.get(`/logistic_plans/${self.id}/excel/`, { responseType: 'blob' });
            self.excelData = response.data;
            self.fetchExcelState = done

            if (!self.excelData) return;
            const url = window.URL.createObjectURL(new Blob([self.excelData]));
            console.log(url)
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'logistic_plan.xlsx');
            document.body.appendChild(link);
            link.click();
        } catch (e) {
            console.error(e)    
            self.fetchExcelState = error
        }
    })
}))

const LogisticPlanStore = types.model({
    logisticPlans: types.maybe(PaginationResponse(LogisticPlan)),
    logisticPlansState: types.optional(State, pending),
    newLogisticPlan: types.maybe(LogisticPlan),
    logisticPlan: types.maybe(LogisticPlan),
    logisticPlanState: types.optional(State, pending),
})
.actions(self => ({
    createLogisticPlan:(accountId)=>{
        self.newLogisticPlan = LogisticPlan.create({account: accountId});
    },
    fetchLogisticPlans: flow(function* fetchLogisticPlans(accountId, rowsPerPage, page) {
        self.logisticPlans = undefined
        self.logisticPlansState = pending
        try {
            const response = yield Api.get(`/logistic_plans/?account=${accountId}&limit=${rowsPerPage}&offset=${page*rowsPerPage}`)
            self.logisticPlans = response.data
            self.logisticPlansState = done
        } catch (e) {
            console.error(e)
            self.logisticPlansState = error
        }
    }),
    fetchLogisticPlan: flow(function* fetchLogisticPlan(id) {
        self.logisticPlansState = pending
        self.logisticPlan = undefined;
        try {
            const response = yield Api.get(`/logistic_plans/${id}/`)
            self.logisticPlan = response.data
            self.logisticPlanState = done
        } catch (e) {
            console.error(e)
            self.logisticPlanState = error
        }
    })
}))

export default LogisticPlanStore.create({})



// WEBPACK FOOTER //
// ./src/stores/LogisticPlanStore.js